import { DatePipe } from '@angular/common';
import { escapeHtml, formatRecurrence, isValidUrlWithAutoProtocol, toTitleCase, tryParseJSON } from '@app/shared/utils/helper';
import { MAX_DISPLAY_ITEMS, TEMPLATES } from '../constants/meeting-audit-log.constants';
import { formatOccurrenceId, sanitizeForCSV } from './meeting-audit-value-helpers';
import { formatFileSize } from '@utils/meeting-attachments.util';
import {
  AttendeeSession,
  AuditValueContext,
  RecordingFile,
  RecordingSession,
  RegistrantSnapshot,
  UpdatedByUser,
  UpdatedOccurrence,
  ZoomWebhookEvent,
} from '../interfaces/meeting-audit-log.interface';

/**
 * Format next_steps array as a numbered list of action items
 * Each item typically follows the format "Person: Action description"
 * Limits display to MAX_DISPLAY_ITEMS and shows "show more" indicator
 */
export function formatNextSteps(steps: string[], forCSV: boolean): string {
  if (steps.length === 0) return '—';

  if (forCSV) {
    return steps.map((step, i) => `${i + 1}. ${sanitizeForCSV(step)}`).join('; ');
  }

  const displaySteps = steps.slice(0, MAX_DISPLAY_ITEMS);
  const hasMore = steps.length > MAX_DISPLAY_ITEMS;
  const remainingCount = steps.length - MAX_DISPLAY_ITEMS;

  const formattedSteps = displaySteps
    .map((step, i) => {
      const escapedStep = escapeHtml(step);
      const colonIndex = escapedStep.indexOf(':');
      if (colonIndex > 0 && colonIndex < 50) {
        const assignee = escapedStep.substring(0, colonIndex);
        const action = escapedStep.substring(colonIndex + 1).trim();
        return TEMPLATES.nextStepWithAssignee(i + 1, assignee, action);
      }
      return TEMPLATES.nextStepSimple(i + 1, escapedStep);
    })
    .join('');

  if (hasMore) {
    return formattedSteps + TEMPLATES.showMoreToggle(remainingCount);
  }

  return formattedSteps;
}

/**
 * Format summary_details array as labeled sections with summaries
 * Each item has a label (topic) and summary (description)
 * Limits display to MAX_DISPLAY_ITEMS and shows "show more" indicator
 */
export function formatSummaryDetails(details: { label: string; summary: string }[], forCSV: boolean): string {
  if (details.length === 0) return '—';

  if (forCSV) {
    return details.map((d, i) => `${i + 1}. ${sanitizeForCSV(d.label)}: ${sanitizeForCSV(d.summary)}`).join('; ');
  }

  const displayDetails = details.slice(0, MAX_DISPLAY_ITEMS);
  const hasMore = details.length > MAX_DISPLAY_ITEMS;
  const remainingCount = details.length - MAX_DISPLAY_ITEMS;

  const formattedDetails = displayDetails
    .map((item, i) => {
      const escapedLabel = escapeHtml(item.label);
      const escapedSummary = escapeHtml(item.summary);
      const isLast = i === displayDetails.length - 1 && !hasMore;
      return TEMPLATES.summaryDetailItem(i + 1, escapedLabel, escapedSummary, isLast);
    })
    .join('');

  if (hasMore) {
    return formattedDetails + TEMPLATES.showMoreToggle(remainingCount);
  }

  return formattedDetails;
}

/**
 * Format updated_by_list array as comma-separated user names
 */
export function formatUpdatedByList(users: UpdatedByUser[]): string {
  return users
    .map((user: UpdatedByUser) => {
      const name = escapeHtml(user.name || user.username || 'Unknown User');
      const email = user.email ? escapeHtml(user.email) : '';
      return email ? `${name} (${email})` : name;
    })
    .join(', ');
}

/**
 * Format sessions array showing join/leave times
 */
export function formatSessions(sessions: AttendeeSession[], forCSV: boolean, datePipe: DatePipe): string {
  const separator = forCSV ? '; ' : '<br><br>';
  return sessions
    .map((session: AttendeeSession, i: number) => {
      const joinTime = session.join_time || session.start_time;
      const leaveTime = session.leave_time || session.end_time;
      const formattedJoin = joinTime ? datePipe.transform(joinTime, 'MMM d, y, h:mm a') : 'N/A';
      const formattedLeave = leaveTime ? datePipe.transform(leaveTime, 'MMM d, y, h:mm a') : 'N/A';
      const prefix = sessions.length > 1 && !forCSV ? `Session ${i + 1}:<br>` : '';
      return forCSV ? `Join Time: ${formattedJoin}, Leave Time: ${formattedLeave}` : `${prefix}Join Time: ${formattedJoin}<br>Leave Time: ${formattedLeave}`;
    })
    .join(separator);
}

/**
 * Format recording files array showing file type, extension, and size
 */
export function formatRecordingFiles(files: RecordingFile[], forCSV: boolean): string {
  if (files.length === 0) return '—';

  const displayFiles = forCSV ? files : files.slice(0, MAX_DISPLAY_ITEMS);
  const hasMore = !forCSV && files.length > MAX_DISPLAY_ITEMS;
  const separator = forCSV ? '; ' : '<br>';

  const formatted = displayFiles
    .map((file: RecordingFile) => {
      const parts: string[] = [];
      const fileType = file.recording_type || file.file_type || 'Unknown';
      const ext = file.file_extension || '';
      parts.push(ext ? `${fileType} (.${ext.toLowerCase()})` : fileType);
      if (file.file_size) {
        parts.push(formatFileSize(file.file_size));
      }
      const joined = parts.join(' — ');
      return forCSV ? joined : escapeHtml(joined);
    })
    .join(separator);

  if (hasMore) {
    return formatted + separator + TEMPLATES.showMoreToggle(files.length - MAX_DISPLAY_ITEMS);
  }
  return formatted;
}

/**
 * Format recording sessions array showing start time, size, and share URL
 */
export function formatRecordingSessions(sessions: RecordingSession[], forCSV: boolean, datePipe: DatePipe): string {
  if (sessions.length === 0) return '—';

  const separator = forCSV ? '; ' : '<br><br>';
  return sessions
    .map((session: RecordingSession, i: number) => {
      const lines: string[] = [];
      const prefix = sessions.length > 1 && !forCSV ? `Session ${i + 1}:` : '';
      if (prefix) lines.push(prefix);

      if (session.start_time) {
        const formattedStart = datePipe.transform(session.start_time, 'MMM d, y, h:mm a') || session.start_time;
        lines.push(forCSV ? `Start Time: ${formattedStart}` : `<strong>Start Time:</strong> ${formattedStart}`);
      }

      if (session.total_size) {
        const size = formatFileSize(session.total_size);
        lines.push(forCSV ? `Size: ${size}` : `<strong>Size:</strong> ${size}`);
      }

      if (session.share_url) {
        if (forCSV) {
          lines.push(`Share URL: ${session.share_url}`);
        } else if (isValidUrlWithAutoProtocol(session.share_url)) {
          const escapedUrl = escapeHtml(session.share_url);
          lines.push(`<strong>Share URL:</strong> <a href="${escapedUrl}" target="_blank" rel="noopener noreferrer"` + ` class="audit-link">${escapedUrl}</a>`);
        } else {
          lines.push(`<strong>Share URL:</strong> ${escapeHtml(session.share_url)}`);
        }
      }

      return forCSV ? lines.join(', ') : lines.join('<br>');
    })
    .join(separator);
}

/**
 * Format mailing_list_group_ids array as comma-separated list
 */
export function formatMailingListGroupIds(groupIds: string[]): string {
  if (groupIds.length === 0) return 'None';
  return groupIds.map((id: string) => escapeHtml(id)).join(', ');
}

/**
 * Format updated_occurrences array showing topic and start time
 */
export function formatUpdatedOccurrences(occurrences: UpdatedOccurrence[], forCSV: boolean, datePipe: DatePipe): string {
  const separator = forCSV ? '; ' : '<br><br>';
  return occurrences
    .map((occurrence: UpdatedOccurrence, i: number) => {
      const lines: string[] = [];
      const prefix = occurrences.length > 1 && !forCSV ? `Occurrence ${i + 1}:` : '';
      if (prefix) lines.push(prefix);

      if (occurrence.topic) {
        const escapedTopic = escapeHtml(occurrence.topic);
        lines.push(forCSV ? `Topic: ${escapedTopic}` : `<strong>Topic:</strong> ${escapedTopic}`);
      }

      const newOccurrenceId = occurrence.new_occurrence_id || occurrence['newOccurrenceId'] || occurrence['new Occurrence Id'];
      if (newOccurrenceId) {
        const formattedNewId = formatOccurrenceId(newOccurrenceId, datePipe);
        lines.push(forCSV ? `Start Time: ${formattedNewId}` : `<strong>Start Time:</strong> ${formattedNewId}`);
      }

      return forCSV ? lines.join(', ') : lines.join('<br>');
    })
    .join(separator);
}

/**
 * Format cancelled_occurrences array showing meeting topic, project, and cancelled date
 */
export function formatCancelledOccurrences(occurrenceIds: string[], forCSV: boolean, datePipe: DatePipe, context?: AuditValueContext): string {
  const separator = forCSV ? '; ' : '<br><br>';
  const meetingTopic = context?.topic || 'Unknown Meeting';
  const projectName = context?.project_name || '';

  return occurrenceIds
    .map((occurrenceId: string, i: number) => {
      const lines: string[] = [];
      const prefix = occurrenceIds.length > 1 && !forCSV ? `Occurrence ${i + 1}: ` : '';
      if (prefix) lines.push(prefix);

      lines.push(forCSV ? `Meeting: ${meetingTopic}` : `<strong>Meeting:</strong> ${escapeHtml(meetingTopic)} `);

      if (projectName) {
        lines.push(forCSV ? `Project: ${projectName}` : `<strong>Project:</strong> ${escapeHtml(projectName)} `);
      }

      const formattedDate = formatOccurrenceId(occurrenceId, datePipe);
      lines.push(forCSV ? `Cancelled Date: ${formattedDate}` : `<strong>Cancelled Date:</strong> ${formattedDate}`);

      return forCSV ? lines.join(', ') : lines.join('<br>');
    })
    .join(separator);
}

/**
 * Format Zoom webhook event payload with meeting details and summary
 * Validates URLs before rendering to prevent XSS via javascript: URLs
 */
export function formatZoomWebhookEvent(event: ZoomWebhookEvent, forCSV: boolean, datePipe: DatePipe): string {
  const payload = event.payload?.object;
  if (!payload) return '—';

  // Raw values (used for CSV — no HTML escaping)
  const rawEventType = event.event || 'Unknown Event';
  const rawMeetingTopic = payload.meeting_topic || payload.summary_title || 'Unknown Meeting';
  const meetingId = String(payload.meeting_id || '');
  const rawHostEmail = payload.meeting_host_email || '';
  const rawSummaryOverview = payload.summary_overview || '';
  const summaryDocUrl = payload.summary_doc_url || '';

  const startTime = payload.meeting_start_time ? (datePipe.transform(payload.meeting_start_time, 'MMM d, y, h:mm a') ?? '') : '';
  const endTime = payload.meeting_end_time ? (datePipe.transform(payload.meeting_end_time, 'MMM d, y, h:mm a') ?? '') : '';

  if (forCSV) {
    const parts = [
      `Event: ${rawEventType}`,
      `Meeting: ${rawMeetingTopic}`,
      meetingId ? `ID: ${meetingId}` : '',
      rawHostEmail ? `Host: ${rawHostEmail}` : '',
      startTime ? `Start: ${startTime}` : '',
      endTime ? `End: ${endTime}` : '',
      rawSummaryOverview ? `Summary: ${rawSummaryOverview}` : '',
    ].filter(Boolean);
    return parts.join('; ');
  }

  // HTML-escaped values for safe rendering
  const eventType = escapeHtml(rawEventType);
  const meetingTopic = escapeHtml(rawMeetingTopic);
  const escapedMeetingId = escapeHtml(meetingId);
  const hostEmail = escapeHtml(rawHostEmail);
  const summaryOverview = escapeHtml(rawSummaryOverview);

  const sections: string[] = [];

  sections.push(TEMPLATES.eventType(eventType.replace(/\./g, ' › ')));
  sections.push(TEMPLATES.meetingDetails(meetingTopic, escapedMeetingId, hostEmail));

  if (startTime || endTime) {
    sections.push(TEMPLATES.timing(startTime, endTime));
  }

  if (summaryOverview) {
    sections.push(TEMPLATES.summaryOverview(summaryOverview));
  }

  if (payload.next_steps && Array.isArray(payload.next_steps) && payload.next_steps.length > 0) {
    sections.push(TEMPLATES.section('Next Steps', formatNextSteps(payload.next_steps, false)));
  }

  if (payload.summary_details && Array.isArray(payload.summary_details) && payload.summary_details.length > 0) {
    sections.push(TEMPLATES.section('Discussion Topics', formatSummaryDetails(payload.summary_details, false)));
  }

  if (summaryDocUrl && isValidUrlWithAutoProtocol(summaryDocUrl)) {
    sections.push(TEMPLATES.externalLink(summaryDocUrl, 'View Full Summary Document'));
  }

  return sections.join('');
}

/**
 * Format registrant snapshot object (captured when a registrant is deleted)
 */
export function formatRegistrantSnapshot(snapshot: RegistrantSnapshot, forCSV: boolean): string {
  const parts: string[] = [];
  const firstName = snapshot.first_name || '';
  const lastName = snapshot.last_name || '';
  const name = [firstName, lastName].filter(Boolean).join(' ');
  if (name) parts.push(forCSV ? `Name: ${name}` : `<strong>Name:</strong> ${escapeHtml(name)}`);
  if (snapshot.email) parts.push(forCSV ? `Email: ${snapshot.email}` : `<strong>Email:</strong> ${escapeHtml(snapshot.email)}`);
  if (snapshot.username) parts.push(forCSV ? `Username: ${snapshot.username}` : `<strong>Username:</strong> ${escapeHtml(snapshot.username)}`);
  if (snapshot.user_id) parts.push(forCSV ? `User ID: ${snapshot.user_id}` : `<strong>User ID:</strong> ${escapeHtml(snapshot.user_id)}`);
  return parts.length > 0 ? parts.join(forCSV ? ', ' : '<br>') : '—';
}

/**
 * Format JSON array strings for specific attributes
 * @returns Formatted string or null if not handled
 */
export function formatJsonArray(data: string, attributeName: string, forCSV: boolean, datePipe: DatePipe, context?: AuditValueContext): string | null {
  const parsed = tryParseJSON(data);
  if (!Array.isArray(parsed)) return null;

  if (attributeName === 'committee_filters') {
    return parsed.length === 0 ? 'None' : parsed.map((item: string) => toTitleCase(item.replace(/_/g, ' '))).join(', ');
  }

  if ((attributeName === 'next_steps' || attributeName === 'edited_next_steps') && parsed.length > 0 && typeof parsed[0] === 'string') {
    return formatNextSteps(parsed, forCSV);
  }

  if ((attributeName === 'summary_details' || attributeName === 'edited_summary_details') && parsed.length > 0 && parsed[0].label) {
    return formatSummaryDetails(parsed, forCSV);
  }

  if (attributeName === 'updated_by_list' && parsed.length > 0) {
    return formatUpdatedByList(parsed);
  }

  if (attributeName === 'sessions' && parsed.length > 0) {
    // Recording sessions have share_url or uuid but no join_time/leave_time
    if (parsed[0].share_url || (parsed[0].uuid && !parsed[0].join_time && !parsed[0].leave_time)) {
      return formatRecordingSessions(parsed, forCSV, datePipe);
    }
    if (parsed[0].join_time || parsed[0].start_time) {
      return formatSessions(parsed, forCSV, datePipe);
    }
  }

  if (attributeName === 'mailing_list_group_ids') {
    return formatMailingListGroupIds(parsed);
  }

  if (attributeName === 'recording_files' && parsed.length > 0) {
    return formatRecordingFiles(parsed, forCSV);
  }

  if (attributeName === 'updated_occurrences' && parsed.length > 0) {
    return formatUpdatedOccurrences(parsed, forCSV, datePipe);
  }

  if (attributeName === 'cancelled_occurrences' && parsed.length > 0) {
    return formatCancelledOccurrences(parsed, forCSV, datePipe, context);
  }

  if (attributeName === 'organizers') {
    if (parsed.length === 0) return 'None';
    return parsed.map((name: string) => escapeHtml(String(name))).join(', ');
  }

  return null;
}

/**
 * Format JSON object strings for specific attributes
 * @returns Formatted string or null if not handled
 */
export function formatJsonObject(data: string, attributeName: string, forCSV: boolean, datePipe: DatePipe): string | null {
  const parsed = tryParseJSON(data);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const normalized = attributeName.toLowerCase();
  if (normalized === 'recurrence') {
    return formatRecurrence(parsed, !forCSV, datePipe);
  }

  if (attributeName === 'zoom_webhook_event' && parsed.event && parsed.payload) {
    return formatZoomWebhookEvent(parsed, forCSV, datePipe);
  }

  if (attributeName === '_registrant_snapshot') {
    return formatRegistrantSnapshot(parsed, forCSV);
  }

  return null;
}
